import { API, Auth } from 'aws-amplify';
import { BENCHMARK_CODE_MAP, SURVEY_INDUSTRY_GROUPINGS, REPORTING_LEVEL_GROUPINGS, SURVEY_BREAKOUTS, SURVEY_STATISTICS } from '../Constants.js';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getAuthToken } from '../utils/AuthUtils';
import './styles.scss';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import Chart from 'chart.js/auto';

const surveyYear = 2026;
const previousSurveyYear = surveyYear - 1;

const valueLabelPlugin = {
    id: 'valueLabelPlugin',
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        const dataset = chart.data.datasets[0];

        ctx.save();
        ctx.font = '600 18px sans-serif';
        ctx.textAlign = 'center';

        meta.data.forEach((bar, index) => {
            const value = dataset.data[index];
            if (value === null || value === undefined) {
                return;
            }

            const label = `${Number(value).toFixed(1)}%`;
            const isNegative = Number(value) < 0;
            const barTop = Math.min(bar.y, bar.base);
            const barBottom = Math.max(bar.y, bar.base);
            ctx.fillStyle = isNegative ? '#c62828' : '#00416A';
            ctx.textBaseline = isNegative ? 'bottom' : 'top';
            ctx.fillText(label, bar.x, isNegative ? barTop - 8 : barBottom + 8);
        });

        ctx.restore();
    }
};

const SurveyOutputDashboardConsole = () => {
    const [selectedRoleCode, setSelectedRoleCode] = useState("CEO");
    const [selectedIndustryGrouping, setSelectedIndustryGrouping] = useState(SURVEY_INDUSTRY_GROUPINGS.ALL);
    const [selectedReportingLevelGrouping, setSelectedReportingLevelGrouping] = useState(REPORTING_LEVEL_GROUPINGS.ALL);
    const [selectedBreakout, setSelectedBreakout] = useState(SURVEY_BREAKOUTS.ALL);
    const [selectedStatistic, setSelectedStatistic] = useState(SURVEY_STATISTICS.MEDIAN);
    const [currentYearData, setCurrentYearData] = useState({});
    const [previousYearData, setPreviousYearData] = useState({});
    const [error, setError] = useState(null);
    const [displayError, setDisplayError] = useState(false);
    const [copyChartStatus, setCopyChartStatus] = useState('idle');
    const yoyChartCanvasRef = useRef(null);
    const yoyChartInstanceRef = useRef(null);


    const handleSetData = (data) => {
        if (data && data.length === 2) {
            if (data[0].year === surveyYear && data[1].year === previousSurveyYear) {
                setCurrentYearData(data[0]);
                setPreviousYearData(data[1]);
            } else if (data[0].year === previousSurveyYear && data[1].year === surveyYear) {
                setCurrentYearData(data[1]);
                setPreviousYearData(data[0]);
            } else {
                console.error('Unexpected year data received:', data);
            }
        } else if (data && data.length === 1) {
            if (data[0].year === surveyYear) {
                setCurrentYearData(data[0]);
            } else if (data[0].year === previousSurveyYear) {
                setPreviousYearData(data[0])
            }
        }
    }

    const checkDataAccess = async () => {
        const user = await Auth.currentAuthenticatedUser();
        let userGroups = user.signInUserSession.accessToken.payload["cognito:groups"];
        return userGroups?.some(group => ['FWC_Admin', 'Internal', 'External', 'Client'].includes(group));
    }

    const handleRetrieveBenchmarkData = useCallback(async () => {
        const dataAccess = await checkDataAccess();
        if (!dataAccess) {
            setError("You do not have access to view this data. Please reachout to data@fwcook.com to request access.");
            setDisplayError(true);
            return;
        }

        const token = await getAuthToken();
        const years = `${surveyYear},${previousSurveyYear}`;
        const queryStringParameters = {
            years,
            roleCode: selectedRoleCode,
            industryGrouping: selectedIndustryGrouping,
            reportingLevelGrouping: selectedReportingLevelGrouping,
            breakout: selectedBreakout,
            statistic: selectedStatistic
        }
        setError(null);
        setDisplayError(false);

        API.get('AnnualCompensations', '/survey-output/benchmark',
            {
                headers: {
                    'Content-Type': 'application/json',
                    Token: token,
                },
                queryStringParameters
            }).then(response => {
                handleSetData(response);
            }).catch(error => {
                console.error('Error retrieving benchmark data:', error);
                setError("Error retrieving benchmark data");
                setDisplayError(true);
            });

    }, [selectedRoleCode, selectedIndustryGrouping, selectedReportingLevelGrouping, selectedBreakout, selectedStatistic]);


    const getYoYChangePercent = (current, previous) => {
        if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) {
            return null;
        }

        return ((current - previous) / previous) * 100;
    };

    const formatYoYChange = (current, previous) => {
        const percentageChange = getYoYChangePercent(current, previous);

        if (percentageChange === null) {
            return 'N/A';
        }

        if (percentageChange < 0) {
            return `(${Math.abs(percentageChange).toFixed(1)}%)`;
        }

        return `${percentageChange.toFixed(1)}%`;
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'number') {
            return Math.round(value);;
        } else {
            return value;
        }
    }

    const isNegativeValue = (value) => {
        if (typeof value === 'number') {
            return value < 0;
        }

        return typeof value === 'string' && value.includes('(');
    }

    const renderTableCell = (value) => (
        <td className={isNegativeValue(value) ? 'negative-value' : ''}>
            {value === null || value === undefined ? '' : value.toLocaleString()}
        </td>
    );

    const shouldUseUnitRevenue = useCallback(() => {
        if (selectedRoleCode.includes("BUO") || selectedRoleCode.includes("DIV")) {
            return true;
        }
        return false;
    }, [selectedRoleCode]);

    const usesUnitRevenue = useMemo(() => shouldUseUnitRevenue(), [shouldUseUnitRevenue]);

    const yoyChartData = useMemo(() => ([
        {
            label: 'Revenue',
            change: usesUnitRevenue
                ? getYoYChangePercent(currentYearData.revenueScopeUnit, previousYearData.revenueScopeUnit)
                : getYoYChangePercent(currentYearData.revenueScopeCorporate, previousYearData.revenueScopeCorporate)
        },
        {
            label: 'Market Cap',
            change: getYoYChangePercent(currentYearData.marketCapFye, previousYearData.marketCapFye)
        },
        {
            label: 'Target TDC',
            change: getYoYChangePercent(currentYearData.targetTotalDirectComp, previousYearData.targetTotalDirectComp)
        },
        {
            label: 'Target LTI',
            change: getYoYChangePercent(currentYearData.targetLtiAmount, previousYearData.targetLtiAmount)
        },
        {
            label: 'Target Cash',
            change: getYoYChangePercent(currentYearData.totalAnnualCompTarget, previousYearData.totalAnnualCompTarget)
        },
        {
            label: 'Salary',
            change: getYoYChangePercent(currentYearData.baseSalary, previousYearData.baseSalary)
        }
    ]), [usesUnitRevenue, currentYearData, previousYearData]);

    const maxAbsYoYChange = useMemo(() => yoyChartData.reduce((maxValue, item) => {
        if (item.change === null) {
            return maxValue;
        }

        return Math.max(maxValue, Math.abs(item.change));
    }, 0), [yoyChartData]);

    const roundedAxisMax = maxAbsYoYChange === 0
        ? 10
        : Math.ceil(maxAbsYoYChange / 5) * 5;

    const dataTable = (
        <table className="benchmark-data-table">
            <thead>
                <tr>
                    <th>Metric</th>
                    <th>{previousSurveyYear}</th>
                    <th>{surveyYear}</th>
                    <th>YoY Change</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Number of Companies</td>
                    {renderTableCell(previousYearData.companiesSampleSize)}
                    {renderTableCell(currentYearData.companiesSampleSize)}
                    {renderTableCell(currentYearData.companiesSampleSize - previousYearData.companiesSampleSize)}
                </tr>
                {usesUnitRevenue ? (
                    <tr>
                        <td>{selectedStatistic} Revenue ($M)</td>
                        {renderTableCell(formatNumber(previousYearData.revenueScopeUnit))}
                        {renderTableCell(formatNumber(currentYearData.revenueScopeUnit))}
                        {renderTableCell(formatYoYChange(currentYearData.revenueScopeUnit, previousYearData.revenueScopeUnit))}
                    </tr>)
                    :
                    (<tr>
                        <td>{selectedStatistic} Revenue ($M)</td>
                        {renderTableCell(formatNumber(previousYearData.revenueScopeCorporate))}
                        {renderTableCell(formatNumber(currentYearData.revenueScopeCorporate))}
                        {renderTableCell(formatYoYChange(currentYearData.revenueScopeCorporate, previousYearData.revenueScopeCorporate))}
                    </tr>
                    )}
                <tr>
                    <td>{selectedStatistic} Market Cap ($M)</td>
                    {renderTableCell(formatNumber(previousYearData.marketCapFye))}
                    {renderTableCell(formatNumber(currentYearData.marketCapFye))}
                    {renderTableCell(formatYoYChange(currentYearData.marketCapFye, previousYearData.marketCapFye))}
                </tr>
                <tr>
                    <td>{selectedStatistic} Target TDC</td>
                    {renderTableCell(formatNumber(previousYearData.targetTotalDirectComp))}
                    {renderTableCell(formatNumber(currentYearData.targetTotalDirectComp))}
                    {renderTableCell(formatYoYChange(currentYearData.targetTotalDirectComp, previousYearData.targetTotalDirectComp))}
                </tr>
                <tr>
                    <td>{selectedStatistic} Target LTI</td>
                    {renderTableCell(formatNumber(previousYearData.targetLtiAmount))}
                    {renderTableCell(formatNumber(currentYearData.targetLtiAmount))}
                    {renderTableCell(formatYoYChange(currentYearData.targetLtiAmount, previousYearData.targetLtiAmount))}
                </tr>
                <tr>
                    <td>{selectedStatistic} Target Cash</td>
                    {renderTableCell(formatNumber(previousYearData.totalAnnualCompTarget))}
                    {renderTableCell(formatNumber(currentYearData.totalAnnualCompTarget))}
                    {renderTableCell(formatYoYChange(currentYearData.totalAnnualCompTarget, previousYearData.totalAnnualCompTarget))}
                </tr>
                <tr>
                    <td>{selectedStatistic} Salary</td>
                    {renderTableCell(formatNumber(previousYearData.baseSalary))}
                    {renderTableCell(formatNumber(currentYearData.baseSalary))}
                    {renderTableCell(formatYoYChange(currentYearData.baseSalary, previousYearData.baseSalary))}
                </tr>
            </tbody>
        </table>
    );

    const handleCopyChartToClipboard = useCallback(async () => {
        const chart = yoyChartInstanceRef.current;
        if (!chart || !chart.canvas || !navigator.clipboard || typeof ClipboardItem === 'undefined') {
            setCopyChartStatus('error');
            return;
        }

        try {
            setCopyChartStatus('copying');
            const blob = await new Promise((resolve) => {
                chart.canvas.toBlob(resolve, 'image/png');
            });

            if (!blob) {
                throw new Error('Unable to create PNG');
            }

            await navigator.clipboard.write([
                new ClipboardItem({
                    'image/png': blob
                })
            ]);

            setCopyChartStatus('copied');
            window.setTimeout(() => {
                setCopyChartStatus('idle');
            }, 2000);
        } catch (copyError) {
            console.error('Error copying chart to clipboard:', copyError);
            setCopyChartStatus('error');
            window.setTimeout(() => {
                setCopyChartStatus('idle');
            }, 2000);
        }
    }, []);

    const yoyBarGraph = (
        <div className="yoy-bar-graph">
            <div className="yoy-bar-graph-header-row">
                <div className="yoy-bar-graph-header">YoY Change</div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="copy-chart-button"
                    onClick={handleCopyChartToClipboard}
                    disabled={copyChartStatus === 'copying'}
                >
                    {copyChartStatus === 'copied' ? 'Copied' : copyChartStatus === 'copying' ? 'Copying...' : 'Copy Chart'}
                </Button>
            </div>
            <div className="yoy-chart-canvas-wrapper">
                <canvas ref={yoyChartCanvasRef} aria-label="YoY change bar chart" role="img" />
            </div>
        </div>
    );

    useEffect(() => {
        const mounted = true;
        if (mounted) {
            setCurrentYearData({});
            setPreviousYearData({});
            setError(null);
            setDisplayError(false);
            handleRetrieveBenchmarkData();
        }
    }, [handleRetrieveBenchmarkData]);

    useEffect(() => {
        const canvas = yoyChartCanvasRef.current;
        if (!canvas) {
            return undefined;
        }

        if (yoyChartInstanceRef.current) {
            yoyChartInstanceRef.current.destroy();
        }

        const labels = yoyChartData.map(({ label }) => label);
        const values = yoyChartData.map(({ change }) => change);
        const backgroundColor = values.map((value) => {
            if (value === null) {
                return '#cbd5e1';
            }

            return value < 0 ? '#c62828' : '#00416A';
        });

        yoyChartInstanceRef.current = new Chart(canvas, {
            type: 'bar',
            plugins: [valueLabelPlugin],
            data: {
                labels,
                datasets: [
                    {
                        label: 'YoY Change (%)',
                        data: values,
                        backgroundColor,
                        borderWidth: 0,
                        borderRadius: 6,
                        maxBarThickness: 44
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                layout: {
                    padding: {
                        bottom: 28
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const value = context.parsed.y;
                                return value === null ? ' N/A' : ` ${value.toFixed(1)}%`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Compensation Metrics',
                            color: '#475569',
                            padding: {
                                top: 16
                            },
                            font: {
                                size: 27,
                                weight: '700'
                            }
                        },
                        ticks: {
                            color: '#334155',
                            font: {
                                size: 18
                            }
                        },
                        grid: {
                            display: false
                        },
                        border: {
                            color: '#64748b',
                            width: 2
                        }
                    },
                    y: {
                        min: -roundedAxisMax,
                        max: roundedAxisMax,
                        title: {
                            display: true,
                            text: 'YoY Change (%)',
                            color: '#475569',
                            font: {
                                size: 27,
                                weight: '700'
                            }
                        },
                        ticks: {
                            stepSize: roundedAxisMax / 2,
                            color: '#64748b',
                            font: {
                                size: 18
                            },
                            callback: (value) => `${value}%`
                        },
                        grid: {
                            color: (context) => (context.tick.value === 0 ? '#64748b' : '#d7dee7'),
                            lineWidth: (context) => (context.tick.value === 0 ? 2 : 1)
                        },
                        border: {
                            color: '#64748b',
                            width: 2
                        }
                    }
                }
            }
        });

        return () => {
            if (yoyChartInstanceRef.current) {
                yoyChartInstanceRef.current.destroy();
                yoyChartInstanceRef.current = null;
            }
        };
    }, [roundedAxisMax, yoyChartData]);

    return (
        <div className="survey-output-dashboard-page">
            <Card className="survey-output-dashboard-console" variant="outlined">
                <Card.Header className="survey-dashboard-header">
                    <div>
                        <div className="survey-dashboard-kicker">EXECUTIVE COMPENSATION INTELLIGENCE</div>
                        <h1>Survey Benchmark</h1>
                    </div>
                    <div className="survey-dashboard-context"><span>DATA COVERAGE</span>{previousSurveyYear}–{surveyYear} surveys</div>
                </Card.Header>
                <Card.Body>
                    <p className="text-muted">Use the filters below to retrieve benchmark data from the {surveyYear} and {previousSurveyYear} surveys. Not every benchmark for every industry and reporting level will be available.</p>
                    <div className="filter-panel">
                        <Form.Group className="filter-group">
                            <Form.Label>Role Code</Form.Label>
                            <Form.Select
                                value={selectedRoleCode}
                                onChange={(e) => setSelectedRoleCode(e.target.value)}
                                aria-label="Select role code"
                            >
                                {Object.entries(BENCHMARK_CODE_MAP).map(([code, label]) => (
                                    <option key={code} value={code}>{label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="filter-group">
                            <Form.Label>Industry Grouping</Form.Label>
                            <Form.Select
                                value={selectedIndustryGrouping}
                                onChange={(e) => setSelectedIndustryGrouping(e.target.value)}
                                aria-label="Select industry grouping"
                            >
                                {Object.values(SURVEY_INDUSTRY_GROUPINGS).map(grouping => (
                                    <option key={grouping} value={grouping}>{grouping}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="filter-group">
                            <Form.Label>Reporting Level Grouping</Form.Label>
                            <Form.Select
                                value={selectedReportingLevelGrouping}
                                onChange={(e) => setSelectedReportingLevelGrouping(e.target.value)}
                                aria-label="Select reporting level grouping"
                            >
                                {Object.values(REPORTING_LEVEL_GROUPINGS).map(grouping => (
                                    <option key={grouping} value={grouping}>{grouping}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="filter-group">
                            <Form.Label>Breakout</Form.Label>
                            <Form.Select
                                value={selectedBreakout}
                                onChange={(e) => setSelectedBreakout(e.target.value)}
                                aria-label="Select breakout"
                            >
                                {Object.values(SURVEY_BREAKOUTS).map(breakout => (
                                    <option key={breakout} value={breakout}>{breakout}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="filter-group">
                            <Form.Label>Statistic</Form.Label>
                            <Form.Select
                                value={selectedStatistic}
                                onChange={(e) => setSelectedStatistic(e.target.value)}
                                aria-label="Select statistic"
                            >
                                {Object.values(SURVEY_STATISTICS).map(statistic => (
                                    <option key={statistic} value={statistic}>{statistic}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </div>

                    {displayError && error && (
                        <Alert variant="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}

                    <div className="data-panel">
                        <div className="data-table-wrapper">
                            {dataTable}
                        </div>
                        {yoyBarGraph}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

export default SurveyOutputDashboardConsole;
